// src/middleware/auth.ts
import { Application, RequestHandler, Request, Response, NextFunction } from 'express';
import { ConfigManager } from '@unidev-hub/config';
import { Logger } from '@unidev-hub/logger';
import jwt from 'jsonwebtoken';
import { expressjwt } from 'express-jwt';
import passport from 'passport';
import { BearerStrategy } from 'passport-azure-ad';
import { HttpError } from './error-handler';
import { AuthOptions, AuthUser } from './types';
import { getConfig } from './config';

// Store the authentication middleware
let authMiddlewareInstance: RequestHandler | null = null;

/**
 * Set up authentication for the application
 * @param app Express application
 * @param config Configuration manager
 * @param logger Logger instance
 * @param options Authentication options
 * @returns Authentication middleware
 */
export async function setupAuth(
  app: Application,
  config: ConfigManager,
  logger: Logger,
  options: AuthOptions = {}
): Promise<RequestHandler> {
  try {
    // Check if auth is required - if not, return pass-through middleware immediately
    const authRequired = options.required ?? config.get<boolean>('auth.required', true);

    if (authRequired === false) {
      logger.info('Authentication is disabled (auth.required=false)');
      return setupNoAuth(app, logger);
    }

    // If custom auth function is provided, use that
    if (options.customAuthFn) {
      logger.info('Using custom authentication provider');
      const middleware = await options.customAuthFn(app, config, logger);
      authMiddlewareInstance = middleware;
      return middleware;
    }

    // Determine auth type from options or config
    const authType = options.type ||
      config.get<string>('auth.type',
        process.env.NODE_ENV === 'production' ? 'jwt' : 'none');

    logger.info(`Setting up authentication with type: ${authType}`);

    let middleware: RequestHandler;

    switch (authType) {
      case 'azure':
        middleware = await setupAzureAuth(app, config, logger, options.azure);
        break;

      case 'jwt':
        middleware = await setupJwtAuth(app, config, logger, options.jwt);
        break;

      case 'none':
      default:
        middleware = setupNoAuth(app, logger);
        break;
    }

    // Store the middleware for later use
    authMiddlewareInstance = middleware;

    return middleware;
  } catch (error) {
    logger.error('Failed to set up authentication', error as Error);
    throw error;
  }
}

/**
 * Authentication middleware
 * @param options Authentication options
 */
export function authenticate(options: { required?: boolean } = {}): RequestHandler {
  return (req: Request, res: Response, next: NextFunction): void => {
    if (!authMiddlewareInstance) {
      // If authentication middleware isn't initialized yet, just pass through
      // This shouldn't happen in production as setupAuth should be called first
      console.warn('Authentication middleware not initialized');
      next();
      return;
    }

    // Get middleware configuration
    const config = getConfig();

    // Skip authentication if not required
    if (options.required === false || config.auth?.required === false) {
      next();
      return;
    }

    // Call the actual authentication middleware
    authMiddlewareInstance(req, res, next);
  };
}

// Azure AD Authentication Setup
interface ITokenPayload {
  oid: string;
  name: string;
  preferred_username: string;
  roles?: string[];
  groups?: string[];
  scp?: string;
}

async function setupAzureAuth(
  app: Application,
  config: ConfigManager,
  logger: Logger,
  options: AuthOptions['azure'] = {}
): Promise<RequestHandler> {
  try {
    // Check if auth is required
    const authRequired = config.get<boolean>('auth.required', true);

    // If auth is not required, return a pass-through middleware
    if (!authRequired) {
      logger.info('Azure authentication is disabled (auth.required=false) - using pass-through middleware');
      return (req: Request, res: Response, next: NextFunction) => next();
    }

    const tenantId = options.tenantId || config.get<string>('auth.azure.tenantId');
    const clientId = options.clientId || config.get<string>('auth.azure.clientId');
    const apiScope = options.apiScope || config.get<string>('auth.azure.apiScope');

    if (!tenantId || !clientId) {
      throw new Error('Azure AD tenant ID and client ID are required');
    }

    logger.info('Initializing Azure AD authentication');

    // Configure Passport to use Azure AD Bearer Strategy
    const bearerStrategy = new BearerStrategy(
      {
        identityMetadata: `https://login.microsoftonline.com/${tenantId}/v2.0/.well-known/openid-configuration`,
        clientID: clientId,
        audience: clientId,
        issuer: `https://sts.windows.net/${tenantId}/`,
        validateIssuer: true,
        loggingLevel: process.env.NODE_ENV === 'development' ? 'info' : 'error',
        loggingNoPII: true,
      },
      (token: any, done: (error: Error | null, user?: any, info?: any) => void) => {
        const tokenPayload = token as ITokenPayload;

        // Verify that token has the expected scope
        const hasScope = tokenPayload.scp?.split(' ').some((s: string) => s === apiScope) ||
          tokenPayload.roles?.some((r: string) => r === apiScope);

        if (apiScope && !hasScope) {
          logger.warn('Token does not have required scope', {
            scope: apiScope,
            tokenScopes: tokenPayload.scp,
            tokenRoles: tokenPayload.roles
          });

          return done(null, null);
        }

        // Store token details in user object for later use
        const user: AuthUser = {
          id: tokenPayload.oid,
          name: tokenPayload.name,
          email: tokenPayload.preferred_username,
          role: tokenPayload.roles?.[0] || 'USER',
          roles: tokenPayload.roles || [],
          permissions: [],
          groups: tokenPayload.groups || [],
          scope: tokenPayload.scp,
          azureToken: tokenPayload
        };

        return done(null, user, token);
      }
    );

    passport.use(bearerStrategy);
    app.use(passport.initialize());

    // Create the authentication middleware
    const authenticateMiddleware = (req: Request, res: Response, next: NextFunction) => {
      passport.authenticate('oauth-bearer', { session: false }, (err: Error | null, user: any, info: any) => {
        if (err) {
          logger.error('Authentication error', err);
          return next(new HttpError(
            'An error occurred during authentication',
            500,
            'AUTHENTICATION_ERROR'
          ));
        }

        if (!user) {
          logger.warn('Authentication failed', {
            info,
            ip: req.ip,
            path: req.path
          });

          return next(new HttpError(
            'Authentication required',
            401,
            'AUTHENTICATION_REQUIRED'
          ));
        }

        // Attach user to request
        req.authUser = user;
        next();
      })(req, res, next);
    };

    logger.info('Azure AD authentication initialized successfully');
    return authenticateMiddleware;
  } catch (error) {
    logger.error('Failed to set up Azure AD authentication', error as Error);
    throw error;
  }
}

// JWT Authentication Setup
async function setupJwtAuth(
  app: Application,
  config: ConfigManager,
  logger: Logger,
  options: AuthOptions['jwt'] = {}
): Promise<RequestHandler> {
  try {
    // Check if auth is required
    const authRequired = config.get<boolean>('auth.required', true);

    // If auth is not required, return a pass-through middleware
    if (!authRequired) {
      logger.info('JWT authentication is disabled (auth.required=false) - using pass-through middleware');
      return (req: Request, res: Response, next: NextFunction) => next();
    }

    // Get JWT options from config or options
    const secret = options.secret || config.get<string>('auth.jwt.secret');
    const publicKey = options.publicKey || config.get<string>('auth.jwt.publicKey');
    const issuer = options.issuer || config.get<string>('auth.jwt.issuer');
    const audience = options.audience || config.get<string>('auth.jwt.audience');

    // We need either a secret or public key
    if (!secret && !publicKey) {
      throw new Error('JWT authentication requires either a secret or public key');
    }

    logger.info('Initializing JWT authentication');

    // Determine which key to use for verification
    const secretOrPublicKey = publicKey || secret;

    // Create JWT verification middleware
    const jwtMiddleware = expressjwt({
      secret: secretOrPublicKey,
      algorithms: ['HS256', 'RS256'],
      issuer: issuer,
      audience: audience,
      requestProperty: 'auth' // Store decoded JWT in req.auth
    });

    // Create authentication middleware that handles errors gracefully
    const authenticateMiddleware = (req: Request, res: Response, next: NextFunction) => {
      jwtMiddleware(req, res, (err) => {
        if (err) {
          if (err.name === 'UnauthorizedError') {
            logger.warn('JWT authentication failed', {
              error: err.message,
              ip: req.ip,
              path: req.path
            });

            return next(new HttpError(
              'Invalid or expired token',
              401,
              'AUTHENTICATION_REQUIRED'
            ));
          }

          logger.error('JWT authentication error', err);
          return next(new HttpError(
            'Authentication error',
            500,
            'INTERNAL_ERROR'
          ));
        }

        // Transfer decoded JWT data to authUser for consistency with other auth methods
        if (req.auth) {
          req.authUser = {
            id: req.auth.sub || req.auth.id,
            role: req.auth.role,
            permissions: req.auth.permissions,
            ...req.auth
          };
        }

        next();
      });
    };

    logger.info('JWT authentication initialized successfully');
    return authenticateMiddleware;
  } catch (error) {
    logger.error('Failed to set up JWT authentication', error as Error);
    throw error;
  }
}

// No Authentication Setup
function setupNoAuth(
  app: Application,
  logger: Logger
): RequestHandler {
  logger.warn('Authentication is disabled - using pass-through middleware');

  // Create a dummy user for development if requested
  if (process.env.NODE_ENV === 'development' && process.env.DEV_AUTH_ENABLED === 'true') {
    logger.warn('Development authentication enabled - using dummy user');

    // Return a middleware that attaches a dummy user to all requests
    return (req: Request, res: Response, next: NextFunction) => {
      req.authUser = {
        id: 'dev-user',
        name: 'Development User',
        email: 'dev@example.com',
        role: 'ADMIN',
        permissions: ['read', 'write', 'delete']
      };
      next();
    };
  }

  // Return a pass-through middleware
  return (req: Request, res: Response, next: NextFunction) => next();
}

/**
 * Role-based authorization middleware
 * @param roles Array of roles allowed to access the resource
 */
export function authorizeRoles(roles: string[]): RequestHandler {
  return (req: Request, res: Response, next: NextFunction) => {
    try {
      if (!req.authUser) {
        throw new HttpError('Authentication required', 401, 'AUTHENTICATION_REQUIRED');
      }

      if (!roles.includes(req.authUser.role || '')) {
        throw new HttpError('Insufficient permissions to access this resource', 403, 'INSUFFICIENT_PERMISSIONS');
      }

      next();
    } catch (error) {
      next(error);
    }
  };
}

/**
 * Permission-based authorization middleware
 * @param requiredPermissions Array of permissions required to access the resource
 */
export function authorizePermissions(requiredPermissions: string[]): RequestHandler {
  return (req: Request, res: Response, next: NextFunction) => {
    try {
      if (!req.authUser) {
        throw new HttpError('Authentication required', 401, 'AUTHENTICATION_REQUIRED');
      }

      // If user is admin, allow access (customize this logic if needed)
      if (req.authUser.role === 'ADMIN') {
        next();
        return;
      }

      // Check if user has all required permissions
      const userPermissions = req.authUser.permissions || [];
      const hasPermission = requiredPermissions.every(permission =>
        userPermissions.includes(permission)
      );

      if (!hasPermission) {
        throw new HttpError('Insufficient permissions to access this resource', 403, 'INSUFFICIENT_PERMISSIONS');
      }

      next();
    } catch (error) {
      next(error);
    }
  };
}

/**
 * Resource ownership validation middleware
 * @param paramIdField The request parameter field containing the resource ID
 * @param userIdField The field in the user object to compare with
 */
export function authorizeOwnership(paramIdField: string = 'id', userIdField: string = 'id'): RequestHandler {
  return (req: Request, res: Response, next: NextFunction) => {
    try {
      if (!req.authUser) {
        throw new HttpError('Authentication required', 401, 'AUTHENTICATION_REQUIRED');
      }

      // If user is admin, allow access
      if (req.authUser.role === 'ADMIN') {
        next();
        return;
      }

      // Check if user is accessing their own resource
      const resourceId = req.params[paramIdField];
      const userId = req.authUser[userIdField];

      if (resourceId !== userId) {
        throw new HttpError('Insufficient permissions to access this resource', 403, 'INSUFFICIENT_PERMISSIONS');
      }

      next();
    } catch (error) {
      next(error);
    }
  };
}
